import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight, ArrowRight } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { useWizard } from "@/seo/WizardContext";
import { waLink } from "@/i18n";
import { Button } from "@/components/ui/button";

export const Breadcrumbs = ({ items }) => (
  <nav data-testid="breadcrumbs" aria-label="Breadcrumb" className="mb-6 flex flex-wrap items-center gap-1.5 text-sm text-slate-500">
    {items.map((it, i) => (
      <span key={it.path} className="flex items-center gap-1.5">
        {i > 0 && <ChevronRight className="h-3.5 w-3.5 text-slate-300" />}
        {i === items.length - 1 ? (
          <span className="font-medium text-slate-700">{it.name}</span>
        ) : (
          <Link to={it.path} className="hover:text-blue-600">{it.name}</Link>
        )}
      </span>
    ))}
  </nav>
);

export const SeoLayout = ({ breadcrumb, children }) => {
  const { openWizard } = useWizard();
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="mx-auto max-w-4xl px-5 pt-28 pb-16 md:px-8 md:pt-36">
        <Breadcrumbs items={breadcrumb} />
        {children}

        {/* Shared CTA */}
        <div className="mt-14 overflow-hidden rounded-[2rem] bg-blue-600 px-6 py-12 text-center md:px-14">
          <h2 className="font-heading text-2xl font-bold text-white sm:text-3xl">Klaar om uw schoonmaak te regelen?</h2>
          <p className="mx-auto mt-3 max-w-xl text-blue-50">Binnen enkele minuten aangevraagd. U ontvangt altijd vooraf een vrijblijvende prijsopgave.</p>
          <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
            <Button
              data-testid="seo-cta-btn"
              onClick={openWizard}
              className="rounded-full bg-white px-8 py-6 text-base font-semibold text-blue-700 hover:bg-blue-50"
            >
              Plan uw schoonmaak <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <a
              href={waLink("Hoi MyCleaner, ik wil graag een schoonmaker aanvragen.")}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center rounded-full border-2 border-white/70 px-8 py-3.5 text-base font-semibold text-white hover:bg-white/10"
            >
              WhatsApp direct
            </a>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export const FaqBlock = ({ faqs }) => (
  <section className="mt-12">
    <h2 className="font-heading text-2xl font-semibold text-slate-900">Veelgestelde vragen</h2>
    <div className="mt-6 space-y-4">
      {faqs.map((f, i) => (
        <div key={i} className="rounded-2xl border border-slate-100 bg-slate-50/60 p-5">
          <h3 className="font-heading text-base font-semibold text-slate-900">{f.q}</h3>
          <p className="mt-2 text-slate-600">{f.a}</p>
        </div>
      ))}
    </div>
  </section>
);

export const Prose = ({ intro, sections }) => (
  <div className="mt-6 space-y-8">
    {intro && <p className="text-lg leading-relaxed text-slate-600">{intro}</p>}
    {sections.map((s, i) => (
      <section key={i}>
        <h2 className="font-heading text-2xl font-semibold text-slate-900">{s.h2}</h2>
        {s.body.map((p, j) => (
          <p key={j} className="mt-3 leading-relaxed text-slate-600">{p}</p>
        ))}
      </section>
    ))}
  </div>
);
